import React from 'react';
import { useCertifications, useSiteContent } from '@personal-portfolio/queries';
import { PageHeader, PageState, PageErrorState, Reveal } from '@/shared/ui';
import { CertificationCard } from './components/certification-card';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation } from 'react-router';
import { toErrorMessage } from '@/shared/lib/query-error';

export const CertificationsPage: React.FC = () => {
  const {
    data: certificationsData,
    isPending: certificationsLoading,
    isFetching: certificationsFetching,
    isPaused: certificationsPaused,
    error: certificationsError,
    refetch: refetchCertifications,
  } = useCertifications();
  const error = toErrorMessage(certificationsError, certificationsPaused);
  const location = useLocation();
  const content = useSiteContent();
  const page = content.pages.certifications;

  const handleRetry = () => {
    if (certificationsFetching) return;
    void refetchCertifications();
  };

  const renderLoading = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="h-24 rounded-xl bg-muted"></div>
          </div>
        ))}
      </div>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <PageErrorState onRetry={handleRetry} message={error ?? undefined} />
    </>
  );

  const renderContent = () => {
    if (!certificationsData?.length) {
      return (
        <>
          <PageHeader title={page.title} subtitle={page.subtitle} />
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-6xl text-muted-foreground">
                <span role="img" aria-label="Trophy">
                  🏆
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                {page.empty.title}
              </h3>
              <p className="text-muted-foreground">{page.empty.message}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader title={page.title} subtitle={page.subtitle} />

        <div className="mb-16 grid gap-5">
          {certificationsData.map((certification) => (
            <Reveal key={certification.id}>
              <CertificationCard certification={certification} />
            </Reveal>
          ))}
        </div>

        <section className="rounded-2xl border border-border bg-muted p-6 sm:p-8">
          <h2 className="mb-8 text-center text-2xl leading-snug font-semibold tracking-tight text-foreground">
            {page.overviewHeading}
          </h2>
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <Reveal
              index={0}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-6"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground">
                {certificationsData.length}
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-card-foreground">
                  {page.stats.total.label}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {page.stats.total.caption}
                </p>
              </div>
            </Reveal>

            <Reveal
              index={1}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-6"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground">
                {certificationsData.filter((cert) => cert.level === 'expert')
                  .length || 0}
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-card-foreground">
                  {page.stats.expert.label}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {page.stats.expert.caption}
                </p>
              </div>
            </Reveal>

            <Reveal
              index={2}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-6 sm:col-span-2 xl:col-span-1"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground">
                {certificationsData.filter(
                  (cert) =>
                    cert.expiryDate && new Date(cert.expiryDate) > new Date(),
                ).length || 0}
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-card-foreground">
                  {page.stats.active.label}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {page.stats.active.caption}
                </p>
              </div>
            </Reveal>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
            <div className="space-y-5 leading-relaxed text-muted-foreground">
              {page.essay.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{page.metaTitle}</title>
        <link
          rel="canonical"
          href={`${content.site.url}${location.pathname}`}
        />
      </Helmet>
      <PageState
        loading={certificationsLoading}
        error={error}
        hasData={!!certificationsData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
};
